import { supabase } from "@/integrations/supabase/client";

/**
 * Lightweight first-party page tracking.
 * Records one row per page view in public.page_views and fills in
 * dwell time when the visitor leaves the page. No cookies — a random
 * session id in sessionStorage groups one browsing session.
 */

const SESSION_KEY = "st_session_id";

const getSessionId = (): string => {
  let id = sessionStorage.getItem(SESSION_KEY);
  if (!id) {
    id = crypto.randomUUID();
    sessionStorage.setItem(SESSION_KEY, id);
  }
  return id;
};

const getDeviceType = (): string => {
  const ua = navigator.userAgent;
  if (/tablet|ipad/i.test(ua)) return "tablet";
  if (/mobi|android|iphone/i.test(ua)) return "mobile";
  return "desktop";
};

let currentViewId: string | null = null;
let viewStartedAt = 0;

const flushDuration = () => {
  if (!currentViewId || !viewStartedAt) return;
  const seconds = Math.round((Date.now() - viewStartedAt) / 1000);
  if (seconds < 1) {
    currentViewId = null;
    return;
  }
  const viewId = currentViewId;
  currentViewId = null;
  // keepalive lets the request finish even while the page is unloading
  const url = import.meta.env.VITE_SUPABASE_URL;
  const key = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY;
  void fetch(`${url}/rest/v1/rpc/record_page_view_duration`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      apikey: key,
      Authorization: `Bearer ${key}`,
    },
    body: JSON.stringify({ _view_id: viewId, _seconds: seconds }),
    keepalive: true,
  }).catch(() => {});
};

/**
 * Call on every route change. Ends the previous page view (recording
 * how long it was open) and starts a new one.
 */
export const trackPageView = async (path: string) => {
  flushDuration();

  viewStartedAt = Date.now();
  currentViewId = null;

  try {
    const { data: authData } = await supabase.auth.getSession();
    // Generate the id client-side: guests may insert but not read rows back,
    // so a returning/select after insert would fail the access rules.
    const viewId = crypto.randomUUID();
    const { error } = await supabase.from("page_views").insert({
      id: viewId,
      path,
      referrer: document.referrer || null,
      session_id: getSessionId(),
      user_id: authData.session?.user?.id || null,
      device_type: getDeviceType(),
    });

    if (!error) {
      currentViewId = viewId;
    } else {
      console.error("Page tracking insert failed:", error.message);
    }
  } catch (err) {
    // Tracking must never break the app
    console.error("Page tracking failed:", err);
  }
};

// Record dwell time when the tab is closed or hidden
if (typeof window !== "undefined") {
  window.addEventListener("pagehide", flushDuration);
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "hidden") flushDuration();
  });
}
